import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { Container, Content, DOMHelper, Message, useToaster } from 'rsuite';
import Header from './Header';
import Sidemenu from './Sidemenu';
import { isExpanded } from '@monorepo/shared/store/sidemenuSlice';
import serviceTermPdf from '/FortiSandbox-Cloud-Service-Description.pdf';
import { Footer } from '@monorepo/shared/components';
import { EnvConst, HomeElms, host, server } from '../config/const';
import {
  getSafeUrl,
  ModalAlert,
  useAppSelector,
  useLazyRegionLoginQuery,
  userPermissionCheck
} from '@monorepo/shared';
import { selectSessionStatus } from '@monorepo/shared/store/authSlice';
import { selectCurrentAccount } from '@monorepo/shared/store/accountSlice';
import { useNavigate } from 'react-router-dom';
import { t } from 'i18next';

const { getHeight, on } = DOMHelper;

export interface FrameProps {
  hideSideMenu?: boolean;
  hideHeader?: boolean;
  hideFooter?: boolean;
  children?: React.ReactNode;
}

const Frame = ({
  children,
  hideSideMenu,
  hideHeader,
  hideFooter
}: FrameProps) => {
  const navigate = useNavigate();
  const [regionLogin, { error, isError }] = useLazyRegionLoginQuery();
  const toaster = useToaster();
  const menuExpanded = useAppSelector(isExpanded);
  const sessionStatus = useAppSelector(selectSessionStatus);
  const currentAccount = useAppSelector(selectCurrentAccount);
  const [windowHeight, setWindowHeight] = useState<number>(getHeight(window));
  const headerHeight = 64;
  const footerHeight = 64;
  const [containerHeight, setContainerHeight] = useState<number>(windowHeight);
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const logOutURL = getSafeUrl(`${server}`);
  const logInUrl = getSafeUrl(
    `${EnvConst.auth_host}/ssologin/?next=${host}/cred/ou`
  );

  useEffect(() => {
    setWindowHeight(getHeight(window));
    const resizeListener = on(window, 'resize', () =>
      setWindowHeight(getHeight(window))
    );

    return () => {
      resizeListener.off();
    };
  }, []);

  useEffect(() => {
    if (sessionStatus === 'EXPIRED') {
      setAlertOpen(true);
    } else if (sessionStatus === 'REVOKED') {
      window.open(getSafeUrl(`${server}`), '_self');
    }
  }, [sessionStatus]);
  useEffect(() => {
    if (currentAccount?.account_id) {
      console.info('------ current account changed', currentAccount);
      const is_check_pass = userPermissionCheck(
        currentAccount,
        HomeElms?.fcName
      );
      if (is_check_pass) {
        if (
          currentAccount?.region?.url &&
          currentAccount?.region?.globalToken
        ) {
          regionLogin({ region: currentAccount?.region });
        }
      } else {
        console.warn('------ user permission check failed', currentAccount);
        if (currentAccount?.user_auth_url && window) {
          window.open(getSafeUrl(`${currentAccount.user_auth_url}`), '_self');
        }
        navigate('/cred/no-permission');
      }
    }
  }, [currentAccount]);
  useEffect(() => {
    if (isError) {
      console.error('Error occurred:', error);
      const regionErrMessage = (
        <Message
          showIcon
          type="warning"
          closable
          header={t('common.apiMsg.warning.region')}
        >
          <span>
            <b>{currentAccount?.region?.alias}</b>
          </span>
        </Message>
      );
      toaster.push(regionErrMessage, {
        placement: 'bottomEnd',
        duration: 5000
      });
    }
  }, [isError, error]);
  const containerClasses = classNames('page-container', {
    'container-full': !menuExpanded
  });

  useEffect(() => {
    let height = windowHeight;
    if (!hideHeader) {
      height -= headerHeight;
    }
    if (!hideFooter) {
      height -= headerHeight;
    }
    setContainerHeight(height);
  }, [hideHeader, hideFooter, windowHeight]);

  return (
    <div style={{ height: windowHeight }}>
      <Header
        hideSideMenu={hideSideMenu}
        style={{
          height: headerHeight,
          display: hideHeader ? 'none' : 'flex'
        }}
      />
      <Container
        className="frame-container"
        style={{ height: containerHeight }}
      >
        <Container className={containerClasses}>
          {!hideSideMenu && <Sidemenu />}
          <Content
            style={{
              height: `${containerHeight}px`
            }}
          >
            {children}
          </Content>
        </Container>
      </Container>
      <Footer
        pdf={serviceTermPdf}
        style={{
          height: footerHeight,
          display: hideFooter ? 'none' : 'flex'
        }}
        server={server}
      ></Footer>
      <ModalAlert
        open={alertOpen}
        setOpen={setAlertOpen}
        logInUrl={logInUrl}
        logOutUrl={logOutURL}
      ></ModalAlert>
    </div>
  );
};

export default Frame;
